import * as React from "react";
import {useEffect, useMemo, useRef, useState} from "react";
import * as d3 from "d3";
import {getAlpha3, getCountryName} from "../../../../utils/countryUtil";
import {numberWithCommas} from "../../../../utils/numberUtil";

interface ICountryLoginRow {
    country: string;
    internal: number;
    external: number;
    total: number;
}

interface ICountryLoginMapProps {
    countryLogins: ICountryLoginRow[];
}

const WORLD_GEOJSON_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

const NO_DATA_FILL = "#E8ECEE";
const HAS_DATA_STROKE = "#007585";
const HAS_DATA_STROKE_WIDTH = 2;
const NO_DATA_STROKE = "#CCD3D6";

function topoToGeo(topo: any): any {
    const obj = topo.objects.countries;
    const arcs = topo.arcs;
    const transform = topo.transform;

    function decodeArc(arcIndex: number): number[][] {
        const reversed = arcIndex < 0;
        const arc = arcs[reversed ? -arcIndex - 1 : arcIndex];
        const coords: number[][] = [];
        let x = 0;
        let y = 0;
        for (const point of arc) {
            x += point[0];
            y += point[1];
            const px = transform ? x * transform.scale[0] + transform.translate[0] : x;
            const py = transform ? y * transform.scale[1] + transform.translate[1] : y;
            coords.push([px, py]);
        }
        if (reversed) coords.reverse();
        return coords;
    }

    function decodeRing(indices: number[]): number[][] {
        const ring: number[][] = [];
        for (const idx of indices) {
            const coords = decodeArc(idx);
            ring.push(...(ring.length > 0 ? coords.slice(1) : coords));
        }
        return ring;
    }

    function decodeGeometry(geom: any): any {
        if (geom.type === "Polygon") {
            return {type: "Polygon", coordinates: geom.arcs.map(decodeRing)};
        } else if (geom.type === "MultiPolygon") {
            return {type: "MultiPolygon", coordinates: geom.arcs.map((polygon: any) => polygon.map(decodeRing))};
        }
        return geom;
    }

    const features = obj.geometries.map((geom: any) => ({
        type: "Feature",
        id: geom.id,
        properties: geom.properties || {},
        geometry: decodeGeometry(geom),
    }));

    return {type: "FeatureCollection", features};
}

const numericToAlpha3: Record<string, string> = {
    "004": "AFG", "008": "ALB", "012": "DZA", "020": "AND", "024": "AGO", "028": "ATG", "032": "ARG", "051": "ARM",
    "036": "AUS", "040": "AUT", "031": "AZE", "044": "BHS", "048": "BHR", "050": "BGD", "052": "BRB", "112": "BLR",
    "056": "BEL", "084": "BLZ", "204": "BEN", "064": "BTN", "068": "BOL", "070": "BIH", "072": "BWA", "076": "BRA",
    "096": "BRN", "100": "BGR", "854": "BFA", "108": "BDI", "132": "CPV", "116": "KHM", "120": "CMR", "124": "CAN",
    "140": "CAF", "148": "TCD", "152": "CHL", "156": "CHN", "170": "COL", "174": "COM", "178": "COG", "180": "COD",
    "188": "CRI", "384": "CIV", "191": "HRV", "192": "CUB", "196": "CYP", "203": "CZE", "208": "DNK", "262": "DJI",
    "212": "DMA", "214": "DOM", "218": "ECU", "818": "EGY", "222": "SLV", "226": "GNQ", "232": "ERI", "233": "EST",
    "748": "SWZ", "231": "ETH", "242": "FJI", "246": "FIN", "250": "FRA", "266": "GAB", "270": "GMB", "268": "GEO",
    "276": "DEU", "288": "GHA", "300": "GRC", "308": "GRD", "320": "GTM", "324": "GIN", "624": "GNB", "328": "GUY",
    "332": "HTI", "340": "HND", "348": "HUN", "352": "ISL", "356": "IND", "360": "IDN", "364": "IRN", "368": "IRQ",
    "372": "IRL", "376": "ISR", "380": "ITA", "388": "JAM", "392": "JPN", "400": "JOR", "398": "KAZ", "404": "KEN",
    "296": "KIR", "408": "PRK", "410": "KOR", "414": "KWT", "417": "KGZ", "418": "LAO", "428": "LVA", "422": "LBN",
    "426": "LSO", "430": "LBR", "434": "LBY", "438": "LIE", "440": "LTU", "442": "LUX", "450": "MDG", "454": "MWI",
    "458": "MYS", "462": "MDV", "466": "MLI", "470": "MLT", "584": "MHL", "478": "MRT", "480": "MUS", "484": "MEX",
    "583": "FSM", "498": "MDA", "492": "MCO", "496": "MNG", "499": "MNE", "504": "MAR", "508": "MOZ", "104": "MMR",
    "516": "NAM", "520": "NRU", "524": "NPL", "528": "NLD", "554": "NZL", "558": "NIC", "562": "NER", "566": "NGA",
    "807": "MKD", "578": "NOR", "512": "OMN", "586": "PAK", "585": "PLW", "591": "PAN", "598": "PNG", "600": "PRY",
    "604": "PER", "608": "PHL", "616": "POL", "620": "PRT", "634": "QAT", "642": "ROU", "643": "RUS", "646": "RWA",
    "659": "KNA", "662": "LCA", "670": "VCT", "882": "WSM", "674": "SMR", "678": "STP", "682": "SAU", "686": "SEN",
    "688": "SRB", "690": "SYC", "694": "SLE", "702": "SGP", "703": "SVK", "705": "SVN", "090": "SLB", "706": "SOM",
    "710": "ZAF", "728": "SSD", "724": "ESP", "144": "LKA", "729": "SDN", "740": "SUR", "752": "SWE", "756": "CHE",
    "760": "SYR", "158": "TWN", "762": "TJK", "834": "TZA", "764": "THA", "626": "TLS", "768": "TGO", "776": "TON",
    "780": "TTO", "788": "TUN", "792": "TUR", "795": "TKM", "798": "TUV", "800": "UGA", "804": "UKR", "784": "ARE",
    "826": "GBR", "840": "USA", "858": "URY", "860": "UZB", "548": "VUT", "862": "VEN", "704": "VNM", "887": "YEM",
    "894": "ZMB", "716": "ZWE", "010": "ATA", "304": "GRL", "540": "NCL", "275": "PSE", "-099": "XKX",
};

function featureToAlpha3(d: any): string | undefined {
    const rawId = d.id;
    const str = String(rawId);
    const padded = str.padStart(3, "0");
    if (/^[A-Z]{3}$/.test(str)) return str;
    return numericToAlpha3[padded] ?? numericToAlpha3[str];
}

export const CountryLoginMap: React.FC<ICountryLoginMapProps> = (props) => {
    const svgRef = useRef<SVGSVGElement>(null);
    const [geoData, setGeoData] = useState<any>(null);
    const [hoveredCountry, setHoveredCountry] = useState<{
        name: string;
        total: number;
        internal: number;
        external: number;
        x: number;
        y: number
    } | null>(null);

    useEffect(() => {
        fetch(WORLD_GEOJSON_URL)
            .then((res) => res.json())
            .then((topo) => setGeoData(topoToGeo(topo)))
            .catch(() => setGeoData(null));
    }, []);

    const loginByAlpha3 = useMemo(() => {
        const map = new Map<string, ICountryLoginRow>();
        props.countryLogins.forEach((row) => {
            const a3 = getAlpha3(row.country);
            if (a3) map.set(a3, row);
        });
        return map;
    }, [props.countryLogins]);

    const maxTotal = useMemo(() => {
        if (props.countryLogins.length === 0) return 1;
        return Math.max(...props.countryLogins.map((r) => r.total));
    }, [props.countryLogins]);

    const colorScale = useMemo(() => {
        return d3.scaleQuantize<string>().domain([1, Math.max(maxTotal, 2)]).range(["#89CDD3", "#29B0C3", "#007585", "#004852"]);
    }, [maxTotal]);

    useEffect(() => {
        if (!geoData || !svgRef.current) return;

        const svg = d3.select(svgRef.current);
        svg.selectAll("*").remove();

        const width = svgRef.current.clientWidth || 700;
        const height = width * 0.52;
        svg.attr("viewBox", `0 0 ${width} ${height}`);

        const projection = d3.geoNaturalEarth1().fitSize([width, height], geoData);
        const pathGen = d3.geoPath().projection(projection);

        const countryGroup = svg.append("g");

        countryGroup
            .selectAll("path")
            .data(geoData.features)
            .enter()
            .append("path")
            .attr("d", (d: any) => pathGen(d) ?? "")
            .attr("fill", (d: any) => {
                const a3 = featureToAlpha3(d);
                const row = a3 ? loginByAlpha3.get(a3) : undefined;
                return row ? colorScale(row.total) : NO_DATA_FILL;
            })
            .attr("stroke", (d: any) => {
                const a3 = featureToAlpha3(d);
                const row = a3 ? loginByAlpha3.get(a3) : undefined;
                return row ? HAS_DATA_STROKE : NO_DATA_STROKE;
            })
            .attr("stroke-width", (d: any) => {
                const a3 = featureToAlpha3(d);
                const row = a3 ? loginByAlpha3.get(a3) : undefined;
                return row ? HAS_DATA_STROKE_WIDTH : 0.5;
            })
            .attr("class", "admin-analytics__map-country")
            .on("mouseenter", function (event: MouseEvent, d: any) {
                countryGroup.selectAll<SVGPathElement, any>("path").each(function (pd: any) {
                    const pa3 = featureToAlpha3(pd);
                    const prow = pa3 ? loginByAlpha3.get(pa3) : undefined;
                    d3.select(this)
                        .attr("stroke", prow ? HAS_DATA_STROKE : NO_DATA_STROKE)
                        .attr("stroke-width", prow ? HAS_DATA_STROKE_WIDTH : 0.5);
                });

                const a3 = featureToAlpha3(d);
                const row = a3 ? loginByAlpha3.get(a3) : undefined;
                const name = d.properties?.name ?? (a3 || "Unknown");

                d3.select(this).attr("stroke", "#004852").attr("stroke-width", 2.5).raise();

                if (row) {
                    const svgRect = svgRef.current!.getBoundingClientRect();
                    setHoveredCountry({
                        name,
                        total: row.total,
                        internal: row.internal,
                        external: row.external,
                        x: event.clientX - svgRect.left,
                        y: event.clientY - svgRect.top,
                    });
                } else {
                    setHoveredCountry(null);
                }
            })
            .on("mousemove", (event: MouseEvent) => {
                const svgRect = svgRef.current!.getBoundingClientRect();
                setHoveredCountry((prev) => prev ? {
                    ...prev,
                    x: event.clientX - svgRect.left,
                    y: event.clientY - svgRect.top
                } : null);
            })
            .on("mouseleave", function () {
                const a3 = featureToAlpha3(d3.select<SVGPathElement, any>(this).datum());
                const row = a3 ? loginByAlpha3.get(a3) : undefined;
                d3.select(this)
                    .attr("stroke", row ? HAS_DATA_STROKE : NO_DATA_STROKE)
                    .attr("stroke-width", row ? HAS_DATA_STROKE_WIDTH : 0.5);
                setHoveredCountry(null);
            });

        const labelGroup = svg.append("g").attr("class", "admin-analytics__map-labels");
        geoData.features.forEach((feature: any) => {
            const a3 = featureToAlpha3(feature);
            const row = a3 ? loginByAlpha3.get(a3) : undefined;
            if (!row) return;

            const centroid = pathGen.centroid(feature);
            if (!centroid || isNaN(centroid[0]) || isNaN(centroid[1])) return;

            const r = Math.max(14, Math.min(22, 10 + String(row.total).length * 4));

            labelGroup.append("circle")
                .attr("cx", centroid[0])
                .attr("cy", centroid[1])
                .attr("r", r)
                .attr("class", "admin-analytics__map-bubble");

            labelGroup.append("text")
                .attr("x", centroid[0])
                .attr("y", centroid[1])
                .attr("class", "admin-analytics__map-bubble-text")
                .text(row.total);
        });
    }, [geoData, loginByAlpha3, colorScale]);

    if (!geoData) {
        return <div className="admin-analytics__map-loading">Loading map...</div>;
    }

    const countriesWithData = props.countryLogins.filter((r) => r.total > 0);

    return (
        <div className="admin-analytics__map-container" data-testid="admin-analytics__map-container">
            <svg ref={svgRef} className="admin-analytics__map-svg" data-testid="admin-analytics__map-svg" role="img"
                 aria-label="World map showing login distribution by country"/>
            {hoveredCountry && (
                <div className="admin-analytics__map-tooltip"
                     style={{left: hoveredCountry.x + 12, top: hoveredCountry.y - 10}}>
                    <div className="admin-analytics__map-tooltip-title">{hoveredCountry.name}</div>
                    <div className="admin-analytics__map-tooltip-row">
                        <span>Total:</span><strong>{numberWithCommas(hoveredCountry.total)}</strong></div>
                    <div className="admin-analytics__map-tooltip-row">
                        <span>Internal:</span><span>{numberWithCommas(hoveredCountry.internal)}</span></div>
                    <div className="admin-analytics__map-tooltip-row">
                        <span>External:</span><span>{numberWithCommas(hoveredCountry.external)}</span></div>
                </div>
            )}
            {countriesWithData.length > 0 && (
                <div className="admin-analytics__map-summary" aria-label="Countries with login activity">
                    {countriesWithData.map((row) => (
                        <span key={row.country} className="admin-analytics__map-summary-tag">
                            {getCountryName(row.country)}: <strong>{numberWithCommas(row.total)}</strong>
                        </span>
                    ))}
                </div>
            )}
            <div className="admin-analytics__map-legend" aria-hidden="true">
                <span className="admin-analytics__map-legend-label">No logins</span>
                <span className="admin-analytics__map-legend-swatch"
                      style={{backgroundColor: NO_DATA_FILL, border: `1px solid ${NO_DATA_STROKE}`}}/>
                <span className="admin-analytics__map-legend-label" style={{marginLeft: 12}}>Fewer</span>
                {["#89CDD3", "#29B0C3", "#007585", "#004852"].map((color) => (
                    <span key={color} className="admin-analytics__map-legend-swatch" style={{backgroundColor: color}}/>
                ))}
                <span className="admin-analytics__map-legend-label">More</span>
            </div>
        </div>
    );
};
